import React, { FC, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
  Link,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode.react';
import useBankID from '../mutations/use-bank-id';
import { BankIdOrderType } from '../types';

interface BankIDAuthProps {
  orderType: string
  collectCallback: Function
  signVisibleData?: string
}

const BankIDAuth: FC<BankIDAuthProps> = ({
  orderType,
  collectCallback,
  signVisibleData,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const mobileDevice = useMediaQuery(theme.breakpoints.down('md'))
  const [useQR, setUseQR] = useState<boolean>(true)
  const [collecting, setCollecting] = useState<boolean>(false)
  const [collectorInstance, setCollectorInstance] = useState<NodeJS.Timeout | null>(null)
  const [hintCode, setHintCode] = useState<string>('')
  const [hintError, setHintError] = useState<boolean>(false)
  const collectorInterval = 2000

  const bankIdInstance = useBankID();

  const {
    openBankIdOnThisDevice,
    orderRef,
    orderStatus,
    orderHint,
    autoStartToken,
    qrCode,
    setOrderHint,
    authOrderBankId,
    authOrderBankIdData,
    authOrderBankIdLoading,
    authOrderBankIdError,
    signOrderBankId,
    signOrderBankIdData,
    signOrderBankIdLoading,
    signOrderBankIdError,
    cancelOrderBankId,
    cancelOrderBankIdData,
    cancelOrderBankIdError,
    collectOrderBankId,
    collectOrderBankIdData,
    collectOrderBankIdError,
    collectOrderBankIdLoading
  } = bankIdInstance

  /**
   * Cleanup collector function
   */
  function cleanupCollector(): void {
    if (collectorInstance) {
      clearInterval(collectorInstance)
      setCollectorInstance(null)
    }
  }

  /**
   * Handle order hints
   * @param hint
   */
  function handleOrderHint(hint: string): void {
    if (hint === '') {
      setHintError(false)
      setHintCode('')
      return
    }

    setHintError(orderStatus === 'failed' || orderStatus === 'cancel')

    switch (hint) {
      // custom errors
      case 'missingPendingOrder':
      case 'missingUser':
      case 'cantConnectUser':
      case 'ssnBelongsToOtherUser':
        setHintCode(hint)
        break
      case 'outstandingTransaction':
        setHintCode(useQR ? 'RFA1' : 'RFA13')
        break
      case 'noClient':
        setHintCode('RFA1')
        break
      case 'started':
        setHintCode(useQR ? 'RFA15B' : 'RFA15A')
        break
      case 'userMrtd':
        setHintCode('RFA23')
        break
      case 'userCallConfirm':
        // nothing
        break
      case 'userSign':
        setHintCode('RFA9')
        break
      case 'expiredTransaction':
        setHintCode('RFA8')
        break
      case 'certificateErr':
        setHintCode('RFA16')
        break
      case 'userCancel':
        setHintCode('RFA6')
        break
      case 'cancelled':
        setHintCode('RFA3')
        break
      case 'startFailed':
        setHintCode(useQR ? 'RFA17B' : 'RFA17A')
        break
      case 'userDeclinedCall':
        // nothing
        break
      case 'notSupportedByUserApp':
        // Ask the user to update to the latest version of the BankID client.
        // custom error message but real BankID hintCode
        setHintCode(hint)
        break
      case 'transactionRiskBlocked':
        // nothing
        break
      default:
        if (orderStatus === 'pending') {
          // RFA21 status pending
          setHintCode('RFA21')
        } else if (orderStatus === 'failed') {
          // RFA22 status failed
          setHintCode('RFA22')
        }
        break
    }
  }

  // Auto start token changed
  React.useEffect(() => {
    if (autoStartToken !== '') {
      openBankIdOnThisDevice(autoStartToken)
    }
  }, [autoStartToken])

  // OrderStatus changed
  React.useEffect(() => {
    if (orderStatus === 'initPolling' || orderStatus === 'pending') {
      // Start polling collect
      setCollecting(true)
    } else {
      // Stop polling collect
      setCollecting(false)
    }

    // Run collect callback if orderStatus is complete
    if (orderStatus === 'complete' && typeof collectOrderBankIdData === 'object' && 'collectOrderBankId' in collectOrderBankIdData) {
      collectCallback(bankIdInstance, collectOrderBankIdData.collectOrderBankId)
    }
  }, [orderStatus])

  // orderHint changed
  React.useEffect(() => {
    handleOrderHint(orderHint)
  }, [orderHint])

  // Collecting
  React.useEffect(() => {
    // Start collecting
    if (collecting && orderRef !== '') {
      // Reset order hint
      setOrderHint('')

      if (collectorInstance === null) {
        const id = setInterval(async () => {
          await collectOrderBankId({
            variables: {
              orderRef
            }
          })
        }, collectorInterval)

        setCollectorInstance(id)
      }
    } else {
      // Stop collecting
      cleanupCollector()
    }

    // Cleanup
    return () => {
      // Stop collecting
      cleanupCollector()
    }
  }, [collecting])

  const isLoading = authOrderBankIdLoading || signOrderBankIdLoading || collectOrderBankIdLoading
  const started = orderRef !== '' && orderStatus !== 'cancel' && orderStatus !== 'failed'

  const hintText = hintCode !== '' ? t(`BankID_${hintCode}`) : null
  const hintErrorMarkup = hintError && hintText !== null && hintText !== '' ? (
    <Box mt={3}>
      <Typography align="center" variant="body1" color="textSecondary">{hintText}</Typography>
    </Box>
  ) : null

  // BankID order started
  if (started) {
    // Use BankID on another device - show QR
    if (useQR && qrCode !== undefined && qrCode !== '') {
      return (
        <Box mt={1}>
          <Box display="flex" justifyContent="center" my={3}>
            <QRCode
              size={200}
              value={qrCode}
              includeMargin
            />
          </Box>
          <Box display="flex" justifyContent="center" my={3}>
            <Typography align="center" variant="body1" color="textSecondary">{hintText}</Typography>
          </Box>
          <Box display="flex" justifyContent="center" my={3}>
            <Link
              style={{ cursor: 'pointer' }}
              component="a"
              color="primary"
              onClick={async () => {
                setOrderHint('')
                await cancelOrderBankId({
                  variables: {
                    orderRef: orderRef
                  },
                });
              }}>
              {t('Cancel login')}
            </Link>
          </Box>
        </Box>
      );
    } else {
      // Open BankID in this device
      return (
        <Box mt={1}>
          <Box display="flex" justifyContent="center" mt={3}>
            <CircularProgress size={48} />
          </Box>
          <Box display="flex" justifyContent="center" my={3}>
            <Typography align="center" variant="body1" color="textSecondary">{hintText}</Typography>
          </Box>
          <Box display="flex" justifyContent="center" my={3}>
            <Link
              style={{ cursor: 'pointer' }}
              component="a"
              color="primary"
              onClick={async () => {
                setOrderHint('')
                await cancelOrderBankId({
                  variables: {
                    orderRef: orderRef
                  },
                });
              }}>
              {t('Cancel login')}
            </Link>
          </Box>
        </Box>
      );
    }
  }

  if (mobileDevice) {
    return (
      <>
        <Box>
          <Button
            color="primary"
            fullWidth
            size="large"
            type="button"
            variant="contained"
            disabled={isLoading}
            style={{ textTransform: 'none' }}
            onClick={async () => {
              setHintCode('')
              setHintError(false)
              setUseQR(false)

              if (orderType === BankIdOrderType.Sign) {
                await signOrderBankId({
                  variables: {
                    userVisibleData: signVisibleData
                  },
                });
              } else {
                await authOrderBankId({
                  variables: {
                    useQr: false
                  }
                });
              }
            }}>
            {t('Open BankID')}
          </Button>
        </Box>
        <Box my={3}>
          <Button
            color="primary"
            fullWidth
            size="large"
            type="button"
            variant="outlined"
            disabled={isLoading}
            style={{ textTransform: 'none' }}
            onClick={async () => {
              setHintCode('')
              setHintError(false)
              setUseQR(true)

              if (orderType === BankIdOrderType.Sign) {
                await signOrderBankId({
                  variables: {
                    userVisibleData: signVisibleData
                  },
                });
              } else {
                await authOrderBankId({
                  variables: {
                    useQr: true
                  }
                });
              }
            }}>
            {t('Mobile BankID on another device')}
          </Button>
        </Box>
        {hintErrorMarkup}
      </>
    );
  }

  return (
    <>
      <Box>
        <Button
          color="primary"
          fullWidth
          size="large"
          type="button"
          variant="contained"
          disabled={isLoading}
          style={{ textTransform: 'none' }}
          onClick={async () => {
            setHintCode('')
            setHintError(false)
            setUseQR(true)

            if (orderType === BankIdOrderType.Sign) {
              await signOrderBankId({
                variables: {
                  userVisibleData: signVisibleData
                },
              });
            } else {
              await authOrderBankId({
                variables: {
                  useQr: true
                }
              });
            }
          }}>
          {t('Mobile BankID on another device')}
        </Button>
      </Box>
      <Box my={3}>
        <Button
          color="primary"
          fullWidth
          size="large"
          type="button"
          variant="outlined"
          disabled={isLoading}
          style={{ textTransform: 'none' }}
          onClick={async () => {
            setHintCode('')
            setHintError(false)
            setUseQR(false)

            if (orderType === BankIdOrderType.Sign) {
              await signOrderBankId({
                variables: {
                  userVisibleData: signVisibleData
                },
              });
            } else {
              await authOrderBankId({
                variables: {
                  useQr: false
                }
              });
            }
          }}>
          {t('Open BankID')}
        </Button>
      </Box>
      {hintErrorMarkup}
    </>
  );
};

BankIDAuth.propTypes = {
  orderType: PropTypes.oneOf(Object.values(BankIdOrderType)).isRequired,
  collectCallback: PropTypes.func.isRequired,
  signVisibleData: PropTypes.string
};

export default BankIDAuth;
